export * from './types';
export * from './constants';

import type {
  CheckoutItemInput,
  CheckoutResult,
  CommerceOrder,
  CommerceOrderEvent,
  CommerceOrderFile,
  CommerceOrderItem,
  CommerceProduct,
  CommerceSupabase,
  CreateOrderResult,
} from './types';

/**
 * Open the shared Stripe customer billing portal (invoices, payment methods,
 * subscriptions). Works in every ReHub app because all apps share one Supabase
 * project and the `customer-portal` edge function + `profiles.stripe_customer_id`.
 *
 * Returns `{ url }` to redirect to, or `{ noCustomer: true }` when the user has
 * never paid (no Stripe customer yet) so the UI can show a friendly notice.
 */
export async function openBillingPortal(
  supabase: CommerceSupabase,
  returnUrl?: string
): Promise<{ url: string } | { noCustomer: true }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('stripe_customer_id')
    .eq('id', user.id)
    .single();
  if (profileError) throw profileError;

  const customerId = (profile as { stripe_customer_id?: string | null } | null)?.stripe_customer_id;
  if (!customerId) return { noCustomer: true };

  const finalReturnUrl =
    returnUrl || (typeof window !== 'undefined' ? window.location.href : 'https://hub.rehub.studio');

  const { data, error } = await supabase.functions.invoke('customer-portal', {
    body: { customerId, returnUrl: finalReturnUrl },
    headers: { 'Content-Type': 'application/json' },
  });
  if (error) throw error;
  const result = data as { url?: string; error?: string } | null;
  if (result?.error) throw new Error(result.error);
  if (!result?.url) throw new Error('Could not open billing portal');
  return { url: result.url };
}

export async function getStoreBySlug(
  supabase: CommerceSupabase,
  slug: string
): Promise<{ id: string; slug: string; name: string; currency_default: string } | null> {
  const { data, error } = await supabase
    .from('commerce_stores')
    .select('id, slug, name, currency_default')
    .eq('slug', slug)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

export async function getActiveProducts(
  supabase: CommerceSupabase,
  storeSlug: string,
  category?: string
): Promise<CommerceProduct[]> {
  const store = await getStoreBySlug(supabase, storeSlug);
  if (!store) return [];

  let query = supabase
    .from('commerce_products')
    .select('*')
    .eq('store_id', store.id)
    .eq('active', true)
    .order('sort_order', { ascending: true });

  if (category && category !== 'All') {
    query = query.eq('category', category);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[commerce] getActiveProducts', error);
    return [];
  }
  return (data ?? []) as CommerceProduct[];
}

export async function getProductBySlug(
  supabase: CommerceSupabase,
  storeSlug: string,
  productSlug: string
): Promise<CommerceProduct | null> {
  const store = await getStoreBySlug(supabase, storeSlug);
  if (!store) return null;
  const { data, error } = await supabase
    .from('commerce_products')
    .select('*')
    .eq('store_id', store.id)
    .eq('slug', productSlug)
    .eq('active', true)
    .maybeSingle();
  if (error || !data) return null;
  return data as CommerceProduct;
}

export async function createCheckoutOrder(
  supabase: CommerceSupabase,
  params: {
    storeSlug: string;
    items: CheckoutItemInput[];
    userId?: string | null;
    guestEmail?: string | null;
    customerNotes?: string | null;
    projectDetails?: Record<string, unknown>;
  }
): Promise<CreateOrderResult> {
  const { data, error } = await supabase.rpc('create_commerce_checkout_order', {
    p_store_slug: params.storeSlug,
    p_items: params.items,
    p_user_id: params.userId ?? null,
    p_guest_email: params.guestEmail ?? null,
    p_customer_notes: params.customerNotes ?? null,
    p_project_details: params.projectDetails ?? {},
  });

  if (error) {
    return { success: false, error: error.message };
  }
  return data as CreateOrderResult;
}

export async function payOrderWithCredits(
  supabase: CommerceSupabase,
  orderId: string,
  userId: string
): Promise<{ success: boolean; error?: string; status?: string }> {
  const { data, error } = await supabase.rpc('pay_commerce_order_with_credits', {
    p_order_id: orderId,
    p_user_id: userId,
  });
  if (error) return { success: false, error: error.message };
  const result = data as { success: boolean; error?: string; status?: string };
  return result;
}

export async function claimGuestOrders(
  supabase: CommerceSupabase,
  userId: string
): Promise<number> {
  const { data, error } = await supabase.rpc('claim_commerce_guest_orders', {
    p_user_id: userId,
  });
  if (error) {
    console.error('[commerce] claimGuestOrders', error);
    return 0;
  }
  return (data as { claimed?: number })?.claimed ?? 0;
}

export async function startCommerceCheckout(
  supabase: CommerceSupabase,
  params: {
    orderId: string;
    userId?: string | null;
    guestEmail?: string | null;
    successUrl: string;
    cancelUrl: string;
  }
): Promise<CheckoutResult> {
  try {
    const { data, error } = await supabase.functions.invoke('api-payment', {
      body: {
        mode: 'commerce_order',
        order_id: params.orderId,
        userId: params.userId ?? undefined,
        guest_email: params.guestEmail ?? undefined,
        successUrl: params.successUrl,
        cancelUrl: params.cancelUrl,
      },
    });

    if (error) {
      let message = error.message || 'CHECKOUT_FAILED';
      try {
        const ctx = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
        if (ctx?.error) message = ctx.error;
      } catch {
        // ignore
      }
      return { error: message };
    }

    if (data?.paid) return { paid: true, orderId: params.orderId };
    if (!data?.url) return { error: data?.error || 'CHECKOUT_FAILED' };
    return { url: data.url as string, orderId: params.orderId };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'CHECKOUT_FAILED';
    return { error: message };
  }
}

export async function getUserOrders(
  supabase: CommerceSupabase,
  userId: string
): Promise<CommerceOrder[]> {
  const { data, error } = await supabase
    .from('commerce_orders')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[commerce] getUserOrders', error);
    return [];
  }
  return (data ?? []) as CommerceOrder[];
}

export async function getOrderById(
  supabase: CommerceSupabase,
  orderId: string
): Promise<CommerceOrder | null> {
  const { data, error } = await supabase
    .from('commerce_orders')
    .select('*')
    .eq('id', orderId)
    .maybeSingle();
  if (error || !data) return null;
  return data as CommerceOrder;
}

export async function getOrderItems(
  supabase: CommerceSupabase,
  orderId: string
): Promise<CommerceOrderItem[]> {
  const { data, error } = await supabase
    .from('commerce_order_items')
    .select('*')
    .eq('order_id', orderId);
  if (error) return [];
  return (data ?? []) as CommerceOrderItem[];
}

export async function getOrderEvents(
  supabase: CommerceSupabase,
  orderId: string
): Promise<CommerceOrderEvent[]> {
  const { data, error } = await supabase
    .from('commerce_order_events')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data ?? []) as CommerceOrderEvent[];
}

export async function getOrderFiles(
  supabase: CommerceSupabase,
  orderId: string
): Promise<CommerceOrderFile[]> {
  const { data, error } = await supabase
    .from('commerce_order_files')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data ?? []) as CommerceOrderFile[];
}

export async function addOrderNote(
  supabase: CommerceSupabase,
  orderId: string,
  message: string
): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase.from('commerce_order_events').insert({
    order_id: orderId,
    user_id: userData.user?.id ?? null,
    event_type: 'note',
    message,
    is_internal: false,
  });
  return !error;
}

export async function uploadOrderFile(
  supabase: CommerceSupabase,
  params: {
    orderId: string;
    userId: string;
    file: File;
  }
): Promise<{ success: boolean; error?: string; file?: CommerceOrderFile }> {
  const path = `${params.orderId}/uploads/${Date.now()}-${params.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

  const { error: uploadError } = await supabase.storage
    .from('commerce-orders')
    .upload(path, params.file, { upsert: false });

  if (uploadError) {
    return { success: false, error: uploadError.message };
  }

  const { data, error } = await supabase
    .from('commerce_order_files')
    .insert({
      order_id: params.orderId,
      user_id: params.userId,
      kind: 'upload',
      file_name: params.file.name,
      storage_path: path,
      mime_type: params.file.type || null,
      size_bytes: params.file.size,
    })
    .select()
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, file: data as CommerceOrderFile };
}

export async function getOrderFileUrl(
  supabase: CommerceSupabase,
  storagePath: string,
  expiresIn = 3600
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('commerce-orders')
    .createSignedUrl(storagePath, expiresIn);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function updateOrderCustomerNotes(
  supabase: CommerceSupabase,
  orderId: string,
  notes: string
): Promise<boolean> {
  const { error } = await supabase
    .from('commerce_orders')
    .update({ customer_notes: notes })
    .eq('id', orderId);
  return !error;
}

/** Admin: list all commerce orders with optional status filter */
export async function adminListOrders(
  supabase: CommerceSupabase,
  status?: string
): Promise<CommerceOrder[]> {
  let query = supabase
    .from('commerce_orders')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  const { data, error } = await query;
  if (error) return [];
  return (data ?? []) as CommerceOrder[];
}

export async function adminUpdateOrderStatus(
  supabase: CommerceSupabase,
  orderId: string,
  status: string,
  userId: string,
  message?: string
): Promise<boolean> {
  const updates: Record<string, unknown> = { status };
  if (status === 'completed') {
    updates.completed_at = new Date().toISOString();
  }
  const { error: orderError } = await supabase
    .from('commerce_orders')
    .update(updates)
    .eq('id', orderId);
  if (orderError) return false;

  await supabase.from('commerce_order_events').insert({
    order_id: orderId,
    user_id: userId,
    event_type: 'status_change',
    message: message || `Status updated to ${status}`,
    is_internal: false,
  });
  return true;
}

export async function adminAddInternalNote(
  supabase: CommerceSupabase,
  orderId: string,
  userId: string,
  message: string
): Promise<boolean> {
  const { error } = await supabase.from('commerce_order_events').insert({
    order_id: orderId,
    user_id: userId,
    event_type: 'internal_note',
    message,
    is_internal: true,
  });
  return !error;
}

export async function adminUploadDeliverable(
  supabase: CommerceSupabase,
  params: {
    orderId: string;
    adminUserId: string;
    file: File;
  }
): Promise<boolean> {
  const path = `${params.orderId}/deliverables/${Date.now()}-${params.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const { error: uploadError } = await supabase.storage
    .from('commerce-orders')
    .upload(path, params.file, { upsert: false });
  if (uploadError) return false;

  const { error } = await supabase.from('commerce_order_files').insert({
    order_id: params.orderId,
    user_id: params.adminUserId,
    kind: 'deliverable',
    file_name: params.file.name,
    storage_path: path,
    mime_type: params.file.type || null,
    size_bytes: params.file.size,
  });
  return !error;
}

export function routeFulfilment(order: CommerceOrder, items: CommerceOrderItem[]): string | null {
  const primary = items[0];
  if (!primary) return null;
  const targetApp = (primary.metadata?.target_app as string) || null;
  if (order.status === 'awaiting_files' || order.status === 'submitted') {
    return `https://staging.rehub.studio/orders/${order.id}`;
  }
  if (primary.fulfilment_type === 'ai_generation' && targetApp) {
    const appUrls: Record<string, string> = {
      staging: 'https://staging.rehub.studio/',
      photoclear: 'https://photoclear.rehub.studio/',
      hyperdusk: 'https://hyperdusk.rehub.studio/',
      hdrphoto: 'https://hdrphoto.rehub.studio/',
      imagemotion: 'https://imagemotion.rehub.studio/',
      tours: 'https://360tours.rehub.studio/',
    };
    return appUrls[targetApp] ?? 'https://staging.rehub.studio/';
  }
  return `https://staging.rehub.studio/orders/${order.id}`;
}
